import { Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { post } from "../../utils/io";

function RequiredLabel({ children }) {
  return (
    <Box
      sx={{
        fontWeight: "bold",
        display: "flex",
        marginBottom: "10px",
      }}
    >
      {children}
      <Box
        component="span"
        sx={{
          fontSize: "15px",
          fontStyle: "Italic",
          justifyContent: "center",
          textAlign: "center",
          color: red[500],
        }}
      >
        *
      </Box>
    </Box>
  );
}

RequiredLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

function FormField({ children }) {
  return <Box sx={{ marginBottom: "15px" }}>{children}</Box>;
}

FormField.propTypes = {
  children: PropTypes.node.isRequired,
};

function CreateAddressModal({ open, onClose, onSuccess, customerId, orgId }) {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      line1: "",
      line2: "",
      city: "",
      province: "",
      postal_code: "",
      country: "United States",
      primary: false,
      shipping: true,
    },
  });

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const addressData = {
        ...data,
        ...(orgId && { org_id: orgId }),
        ...(customerId && { customer_id: customerId }),
      };

      const res = await post("/admin/customer_addresses", addressData);

      if (res.status === 201 || res.status === 200) {
        enqueueSnackbar("Address created successfully", {
          variant: "success",
        });
        handleClose();
        onSuccess?.(res.data.address);
      } else {
        enqueueSnackbar("Failed to create address", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || "Error creating address", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const validationRules = {
    title: {
      required: "Title is required",
    },
    line1: {
      required: "Address line 1 is required",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: "400px" },
      }}
    >
      <DialogTitle>
        Create New Address
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormField>
            <RequiredLabel>Address Title/Name</RequiredLabel>
            <Controller
              name="title"
              control={control}
              rules={validationRules.title}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="e.g., Main Office, Warehouse, etc."
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            />
          </FormField>

          <FormField>
            <RequiredLabel>Address Line 1</RequiredLabel>
            <Controller
              name="line1"
              control={control}
              rules={validationRules.line1}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Street address"
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  error={!!errors.line1}
                  helperText={errors.line1?.message}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            />
          </FormField>

          <FormField>
            <Box
              sx={{
                fontWeight: "bold",
                display: "flex",
                marginBottom: "10px",
              }}
            >
              Address Line 2
            </Box>
            <Controller
              name="line2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Apartment, suite, etc. (optional)"
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            />
          </FormField>

          <FormField>
            <Box
              sx={{
                fontWeight: "bold",
                display: "flex",
                marginBottom: "10px",
              }}
            >
              City
            </Box>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="City"
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            />
          </FormField>

          <Box sx={{ display: "flex", gap: 2, marginBottom: "15px" }}>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  marginBottom: "10px",
                }}
              >
                State/Province
              </Box>
              <Controller
                name="province"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="State or Province"
                    disabled={loading}
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  marginBottom: "10px",
                }}
              >
                ZIP/Postal Code
              </Box>
              <Controller
                name="postal_code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="ZIP or Postal Code"
                    disabled={loading}
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100%",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          <FormField>
            <Box
              sx={{
                fontWeight: "bold",
                display: "flex",
                marginBottom: "10px",
              }}
            >
              Country
            </Box>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Country"
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            />
          </FormField>

          <FormField>
            <Controller
              name="primary"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={loading}
                    />
                  }
                  label="Set as primary address"
                />
              )}
            />
          </FormField>

          <FormField>
            <Controller
              name="shipping"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={loading}
                    />
                  }
                  label="Use for shipping"
                />
              )}
            />
          </FormField>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={loading}
        >
          Create Address
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

CreateAddressModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  customerId: PropTypes.number,
  orgId: PropTypes.number,
};

CreateAddressModal.defaultProps = {
  onSuccess: undefined,
  customerId: undefined,
  orgId: undefined,
};

export default CreateAddressModal;
